/**
 * Hotspot Position TypeScript Module for TYPO3 13
 * Manages interactive hotspot positioning in the TYPO3 backend
 */

// Constants for timing and retry behavior
const TIMING = {
    INIT_DELAY: 500,
    ELEMENT_RETRY_DELAY: 100,
    ELEMENT_SETUP_DELAY: 1000,
    NEW_ITEM_POLL_DELAY: 200,
    MAX_RETRIES: 50
} as const;

// Selectors for better maintainability
const SELECTORS = {
    SCOPE: '[data-foreign-table="tx_hbhotspot_hotspots"]',
    TRIGGER: '[data-foreign-table="tx_hbhotspot_hotspots"] [data-object-uid]',
    CREATE_BUTTON: '[data-foreign-table="tx_hbhotspot_hotspots"] .t3js-create-new-button',
    HOTSPOT_IMAGE: (key: string) => `#hotspot-position-${key}`,
    INPUT_X: (key: string) => `[name="data[tx_hbhotspot_hotspots][${key}][tx_hbhotspot_position_x]"]`,
    INPUT_Y: (key: string) => `[name="data[tx_hbhotspot_hotspots][${key}][tx_hbhotspot_position_y]"]`,
    VISIBLE_INPUT_X: (key: string) => `[data-formengine-input-name="data[tx_hbhotspot_hotspots][${key}][tx_hbhotspot_position_x]"]`,
    VISIBLE_INPUT_Y: (key: string) => `[data-formengine-input-name="data[tx_hbhotspot_hotspots][${key}][tx_hbhotspot_position_y]"]`,
    INDICATOR: (key: string) => `#hotspot-position-${key} [data-indicator]`
} as const;

interface HotspotElements {
    inputX: HTMLInputElement;
    inputY: HTMLInputElement;
    visibleInputX: HTMLInputElement;
    visibleInputY: HTMLInputElement;
    indicator: HTMLElement;
}

interface Position {
    x: number;
    y: number;
}

/**
 * Manages hotspot positioning functionality in TYPO3 backend
 */
class HotspotPosition {
    private addedEventListenersToUids: Set<string> = new Set();
    private isInitialized = false;

    constructor() {
        this.init().catch(error => {
            console.error('Failed to initialize HotspotPosition:', error);
        });
    }

    /**
     * Initialize the hotspot position manager
     */
    private async init(): Promise<void> {
        try {
            const scope = this.querySelector<HTMLElement>(SELECTORS.SCOPE);
            if (!scope) {
                return;
            }

            // Wait for DOM to be ready
            await this.delay(TIMING.INIT_DELAY);

            const triggers = this.querySelectorAll<HTMLElement>(SELECTORS.TRIGGER);
            const createButton = this.querySelector<HTMLButtonElement>(SELECTORS.CREATE_BUTTON);

            if (!createButton) {
                return;
            }

            await this.setupEventListeners(triggers, createButton);
            this.isInitialized = true;

        } catch (error) {
            throw new Error(`Initialization failed: ${error instanceof Error ? error.message : String(error)}`);
        }
    }

    /**
     * Setup all event listeners for hotspot management
     */
    private async setupEventListeners(
        triggers: NodeListOf<HTMLElement>,
        createButton: HTMLButtonElement
    ): Promise<void> {
        try {
            // Setup image click handlers
            await this.addEventListenersToAllImages(triggers);

            // Handle new hotspot creation
            this.setupCreateButtonListener(createButton, triggers);

            // Handle existing hotspot clicks
            this.setupTriggerListeners(triggers);

        } catch (error) {
            throw new Error(`Event listener setup failed: ${error instanceof Error ? error.message : String(error)}`);
        }
    }

    /**
     * Add event listeners to all hotspot images
     */
    private async addEventListenersToAllImages(triggers: NodeListOf<HTMLElement>): Promise<void> {
        const promises = Array.from(triggers).map(async (item) => {
            try {
                const key = item.dataset.objectUid;
                if (!key || this.addedEventListenersToUids.has(key)) {
                    return;
                }

                const image = this.querySelector<HTMLElement>(SELECTORS.HOTSPOT_IMAGE(key));
                if (!image) {
                    return;
                }

                this.setupImageClickHandler(image, key);
                await this.setupElementPositioning(key);
                this.addedEventListenersToUids.add(key);

            } catch (error) {
                console.warn(`Failed to setup event listener for hotspot: ${error instanceof Error ? error.message : String(error)}`);
            }
        });

        await Promise.allSettled(promises);
    }

    /**
     * Setup click handler for hotspot image
     */
    private setupImageClickHandler(image: HTMLElement, key: string): void {
        image.addEventListener('click', (event: MouseEvent) => {
            try {
                this.handleImageClick(event, key);
            } catch (error) {
                console.error(`Image click handler failed for hotspot ${key}:`, error);
            }
        });
    }

    /**
     * Handle click on hotspot image to set position
     */
    private handleImageClick(event: MouseEvent, key: string): void {
        const target = event.target as HTMLElement;
        if (!target) {
            throw new Error('Event target is null');
        }

        const elements = this.getHotspotElements(key);
        if (!elements) {
            throw new Error(`Could not find required elements for hotspot ${key}`);
        }

        const position = this.calculatePosition(event, target);
        this.updatePosition(elements, position);
    }

    /**
     * Calculate position from click coordinates
     */
    private calculatePosition(event: MouseEvent, target: HTMLElement): Position {
        const rect = target.getBoundingClientRect();
        const rectWidth = target.offsetWidth;
        const rectHeight = target.offsetHeight;

        if (rectWidth === 0 || rectHeight === 0) {
            throw new Error('Target element has zero dimensions');
        }

        const x = (event.clientX - rect.left) / rectWidth * 100;
        const y = (event.clientY - rect.top) / rectHeight * 100;

        return {
            x: this.roundToDecimals(Math.max(0, Math.min(100, x)), 2),
            y: this.roundToDecimals(Math.max(0, Math.min(100, y)), 2)
        };
    }

    /**
     * Update position in UI elements
     */
    private updatePosition(elements: HotspotElements, position: Position): void {
        const { x, y } = position;

        // Update indicator position
        elements.indicator.style.left = `${x}%`;
        elements.indicator.style.top = `${y}%`;

        // Update input values
        elements.visibleInputX.value = x.toString();
        elements.visibleInputY.value = y.toString();
        elements.inputX.value = x.toString();
        elements.inputY.value = y.toString();

        // Trigger change events
        elements.visibleInputX.dispatchEvent(new Event('change'));
        elements.visibleInputY.dispatchEvent(new Event('change'));
    }

    /**
     * Setup element positioning with retry mechanism
     */
    private async setupElementPositioning(key: string, retryCount = 0): Promise<void> {
        if (retryCount >= TIMING.MAX_RETRIES) {
            throw new Error(`Max retries exceeded for hotspot ${key}`);
        }

        try {
            const elements = this.getHotspotElements(key);
            if (!elements) {
                await this.delay(TIMING.ELEMENT_RETRY_DELAY);
                return this.setupElementPositioning(key, retryCount + 1);
            }

            this.initializeElementPosition(elements);
            this.setupInputChangeListeners(elements);

        } catch (error) {
            console.warn(`Setup element positioning failed for ${key}, retry ${retryCount + 1}:`, error);
            await this.delay(TIMING.ELEMENT_RETRY_DELAY);
            return this.setupElementPositioning(key, retryCount + 1);
        }
    }

    /**
     * Initialize element position from existing values
     */
    private initializeElementPosition(elements: HotspotElements): void {
        const x = parseFloat(elements.inputX.value) || 0;
        const y = parseFloat(elements.inputY.value) || 0;

        elements.indicator.style.left = `${x}%`;
        elements.indicator.style.top = `${y}%`;
    }

    /**
     * Setup change listeners for visible inputs
     */
    private setupInputChangeListeners(elements: HotspotElements): void {
        elements.visibleInputX.addEventListener('change', () => {
            const value = parseFloat(elements.visibleInputX.value) || 0;
            const clampedValue = Math.max(0, Math.min(100, value));
            elements.indicator.style.left = `${clampedValue}%`;
            elements.inputX.value = clampedValue.toString();
        });

        elements.visibleInputY.addEventListener('change', () => {
            const value = parseFloat(elements.visibleInputY.value) || 0;
            const clampedValue = Math.max(0, Math.min(100, value));
            elements.indicator.style.top = `${clampedValue}%`;
            elements.inputY.value = clampedValue.toString();
        });
    }

    /**
     * Setup create button listener for new hotspots
     */
    private setupCreateButtonListener(
        createButton: HTMLButtonElement,
        initialTriggers: NodeListOf<HTMLElement>
    ): void {
        createButton.addEventListener('click', () => {
            try {
                this.handleCreateButtonClick(initialTriggers.length);
            } catch (error) {
                console.error('Create button click handler failed:', error);
            }
        });
    }

    /**
     * Handle create button click with polling for new elements
     */
    private async handleCreateButtonClick(initialCount: number): Promise<void> {
        const pollForNewElements = async (): Promise<void> => {
            const currentTriggers = this.querySelectorAll<HTMLElement>(SELECTORS.TRIGGER);
            
            if (currentTriggers.length === initialCount) {
                await this.delay(TIMING.NEW_ITEM_POLL_DELAY);
                return pollForNewElements();
            }

            await this.addEventListenersToAllImages(currentTriggers);
        };

        await pollForNewElements();
    }

    /**
     * Setup listeners for existing trigger elements
     */
    private setupTriggerListeners(triggers: NodeListOf<HTMLElement>): void {
        triggers.forEach(item => {
            item.addEventListener('click', () => {
                setTimeout(() => {
                    const currentTriggers = this.querySelectorAll<HTMLElement>(SELECTORS.TRIGGER);
                    this.addEventListenersToAllImages(currentTriggers).catch(error => {
                        console.error('Failed to setup event listeners after trigger click:', error);
                    });
                }, TIMING.ELEMENT_SETUP_DELAY);
            });
        });
    }

    /**
     * Get all required hotspot elements with type safety
     */
    private getHotspotElements(key: string): HotspotElements | null {
        try {
            const inputX = this.querySelector<HTMLInputElement>(SELECTORS.INPUT_X(key));
            const inputY = this.querySelector<HTMLInputElement>(SELECTORS.INPUT_Y(key));
            const visibleInputX = this.querySelector<HTMLInputElement>(SELECTORS.VISIBLE_INPUT_X(key));
            const visibleInputY = this.querySelector<HTMLInputElement>(SELECTORS.VISIBLE_INPUT_Y(key));
            const indicator = this.querySelector<HTMLElement>(SELECTORS.INDICATOR(key));

            if (!inputX || !inputY || !visibleInputX || !visibleInputY || !indicator) {
                return null;
            }

            return { inputX, inputY, visibleInputX, visibleInputY, indicator };

        } catch (error) {
            console.warn(`Failed to get hotspot elements for ${key}:`, error);
            return null;
        }
    }

    /**
     * Type-safe querySelector wrapper
     */
    private querySelector<T extends Element>(selector: string): T | null {
        return document.querySelector<T>(selector);
    }

    /**
     * Type-safe querySelectorAll wrapper
     */
    private querySelectorAll<T extends Element>(selector: string): NodeListOf<T> {
        return document.querySelectorAll<T>(selector);
    }

    /**
     * Promise-based delay utility
     */
    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Round number to specified decimal places
     */
    private roundToDecimals(num: number, decimals: number): number {
        return Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
    }

    /**
     * Public method to check if instance is initialized
     */
    public isReady(): boolean {
        return this.isInitialized;
    }

    /**
     * Public method to manually trigger setup for new elements
     */
    public async refresh(): Promise<void> {
        if (!this.isInitialized) {
            throw new Error('HotspotPosition is not initialized');
        }

        const triggers = this.querySelectorAll<HTMLElement>(SELECTORS.TRIGGER);
        await this.addEventListenersToAllImages(triggers);
    }
}

export default new HotspotPosition();